import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from 'components/AppIcon';

const INTEREST_RATE = 0.15; // 15% per cycle
const MIN_LOAN = 1000;
const MAX_LOAN = 1000000;

const LoanModal = ({ playerCash, ownedBusinesses, activeLoan, onClose, onSuccess }) => {
  const [mode, setMode] = useState(activeLoan ? 'repay' : 'take');
  const [amount, setAmount] = useState('');
  const [repayAmount, setRepayAmount] = useState('');
  const [collateralId, setCollateralId] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    if (ownedBusinesses?.length > 0 && !collateralId) {
      setCollateralId(ownedBusinesses?.[0]?.id || '');
    }
  }, [ownedBusinesses, collateralId]);

  const loanAmount = parseFloat(amount) || 0;
  const interestAmount = loanAmount * INTEREST_RATE;
  const totalRepayable = loanAmount + interestAmount;
  const remainingBalance = parseFloat(activeLoan?.remaining_balance || 0);

  const validateTake = () => {
    const errs = {};
    const amt = parseFloat(amount);
    if (!amount || isNaN(amt)) {
      errs.amount = 'Please enter a loan amount';
    } else if (amt < MIN_LOAN) {
      errs.amount = `Minimum loan amount is $${MIN_LOAN?.toLocaleString()}`;
    } else if (amt > MAX_LOAN) {
      errs.amount = `Maximum loan amount is $${MAX_LOAN?.toLocaleString()}`;
    }
    if (ownedBusinesses?.length === 0) {
      errs.collateral = 'You must own at least one business to take a loan';
    } else if (!collateralId) {
      errs.collateral = 'Please select a business as collateral';
    }
    return errs;
  };

  const validateRepay = () => {
    const errs = {};
    const amt = parseFloat(repayAmount);
    if (!repayAmount || isNaN(amt)) {
      errs.repayAmount = 'Please enter a repayment amount';
    } else if (amt < 1) {
      errs.repayAmount = 'Minimum repayment is $1';
    } else if (amt > remainingBalance) {
      errs.repayAmount = `Cannot repay more than remaining balance ($${remainingBalance?.toLocaleString()})`;
    } else if (amt > playerCash) {
      errs.repayAmount = `Insufficient funds. You have $${playerCash?.toLocaleString()} available`;
    }
    return errs;
  };

  const handleTakeLoan = async () => {
    const errs = validateTake();
    if (Object.keys(errs)?.length > 0) { setErrors(errs); return; }
    setLoading(true);
    setErrors({});
    try {
      const { data, error } = await supabase?.rpc('take_loan', {
        p_amount: loanAmount,
        p_collateral_business_id: collateralId,
      });
      if (error) throw error;
      if (!data?.success) {
        const msg = data?.error?.toLowerCase() || '';
        if (msg?.includes('active loan') || msg?.includes('existing loan')) {
          setErrors({ general: 'You already have an active loan. Repay it before taking a new one.' });
        } else if (msg?.includes('collateral') || msg?.includes('business')) {
          setErrors({ collateral: 'Selected business cannot be used as collateral.' });
        } else {
          setErrors({ general: data?.error || 'Loan application failed.' });
        }
        return;
      }
      setSuccessMsg(`Loan of $${loanAmount?.toLocaleString()} approved! Funds added to your account.`);
      setTimeout(() => { onSuccess?.(); onClose?.(); }, 2000);
    } catch (err) {
      const msg = err?.message?.toLowerCase() || '';
      if (msg?.includes('active loan') || msg?.includes('existing')) {
        setErrors({ general: 'You already have an active loan. Repay it first.' });
      } else {
        setErrors({ general: err?.message || 'Loan application failed.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRepayLoan = async () => {
    const errs = validateRepay();
    if (Object.keys(errs)?.length > 0) { setErrors(errs); return; }
    setLoading(true);
    setErrors({});
    try {
      const { data, error } = await supabase?.rpc('repay_loan', {
        p_loan_id: activeLoan?.id,
        p_amount: parseFloat(repayAmount),
      });
      if (error) throw error;
      if (!data?.success) {
        const msg = data?.error?.toLowerCase() || '';
        if (msg?.includes('insufficient') || msg?.includes('funds')) {
          setErrors({ repayAmount: 'Insufficient funds to make this repayment.' });
        } else {
          setErrors({ general: data?.error || 'Repayment failed.' });
        }
        return;
      }
      const newBalance = remainingBalance - parseFloat(repayAmount);
      setSuccessMsg(newBalance <= 0 ? 'Loan fully repaid! Great work.' : `Payment of $${parseFloat(repayAmount)?.toLocaleString()} applied. Remaining: $${newBalance?.toLocaleString()}`);
      setTimeout(() => { onSuccess?.(); onClose?.(); }, 2000);
    } catch (err) {
      const msg = err?.message?.toLowerCase() || '';
      if (msg?.includes('insufficient') || msg?.includes('funds')) {
        setErrors({ repayAmount: 'Insufficient funds to make this repayment.' });
      } else {
        setErrors({ general: err?.message || 'Repayment failed.' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.8)' }}>
      <div className="w-full max-w-md rounded-2xl border border-border bg-card overflow-hidden shadow-xl">
        {/* Header */}
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center">
              <Icon name="CreditCard" size={20} color="var(--color-accent)" />
            </div>
            <div>
              <h2 className="font-heading text-base font-bold text-foreground">Business Loan</h2>
              <p className="text-xs text-muted-foreground">{INTEREST_RATE * 100}% interest rate</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="p-5">
          {successMsg ? (
            <div className="text-center py-6">
              <div className="w-14 h-14 rounded-full bg-success/10 border border-success/30 flex items-center justify-center mx-auto mb-3">
                <Icon name="CheckCircle" size={28} color="var(--color-success)" />
              </div>
              <p className="text-sm font-semibold text-success">{successMsg}</p>
            </div>
          ) : (
            <>
              {/* Mode tabs */}
              {activeLoan && (
                <div className="flex gap-1 mb-5 bg-surface rounded-lg p-1">
                  {['take', 'repay']?.map(m => (
                    <button
                      key={m}
                      onClick={() => { setMode(m); setErrors({}); }}
                      className={`flex-1 py-2 rounded-md text-xs font-semibold capitalize transition-all ${
                        mode === m ? 'bg-primary text-zinc-950' : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {m === 'take' ? 'New Loan' : 'Repay Loan'}
                    </button>
                  ))}
                </div>
              )}

              {errors?.general && (
                <div className="mb-4 p-3 rounded-lg bg-error/10 border border-error/30 flex items-start gap-2">
                  <Icon name="AlertCircle" size={14} color="var(--color-error)" className="mt-0.5 shrink-0" />
                  <p className="text-xs text-error">{errors?.general}</p>
                </div>
              )}

              {mode === 'take' ? (
                <div className="space-y-4">
                  {activeLoan && (
                    <div className="p-3 rounded-lg bg-warning/10 border border-warning/20">
                      <p className="text-xs text-warning">⚠ You have an active loan of ${remainingBalance?.toLocaleString()} remaining. Taking a new loan may not be possible.</p>
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Loan Amount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      <input
                        type="number"
                        value={amount}
                        onChange={e => { setAmount(e?.target?.value); if (errors?.amount) setErrors(p => ({ ...p, amount: '' })); }}
                        onBlur={() => {
                          const amt = parseFloat(amount);
                          if (amount && !isNaN(amt)) {
                            if (amt < MIN_LOAN) setErrors(p => ({ ...p, amount: `Minimum is $${MIN_LOAN?.toLocaleString()}` }));
                            else if (amt > MAX_LOAN) setErrors(p => ({ ...p, amount: `Maximum is $${MAX_LOAN?.toLocaleString()}` }));
                          }
                        }}
                        placeholder={`${MIN_LOAN?.toLocaleString()} – ${MAX_LOAN?.toLocaleString()}`}
                        min={MIN_LOAN}
                        max={MAX_LOAN}
                        className={`w-full bg-surface border rounded-lg pl-7 pr-3 py-2.5 text-sm text-foreground focus:outline-none transition-colors ${
                          errors?.amount ? 'border-error' : 'border-border focus:border-primary/50'
                        }`}
                      />
                    </div>
                    {errors?.amount && <p className="mt-1 text-xs text-error flex items-center gap-1"><Icon name="AlertCircle" size={12} />{errors?.amount}</p>}
                    <p className="mt-1 text-xs text-muted-foreground">Range: $1,000 – $1,000,000</p>
                  </div>

                  {loanAmount > 0 && (
                    <div className="p-3 rounded-lg bg-surface border border-border space-y-1.5">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Principal</span>
                        <span className="text-foreground font-mono">${loanAmount?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Interest ({INTEREST_RATE * 100}%)</span>
                        <span className="text-warning font-mono">${interestAmount?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-xs border-t border-border pt-1.5">
                        <span className="text-muted-foreground font-semibold">Total Repayable</span>
                        <span className="text-error font-mono font-bold">${totalRepayable?.toLocaleString()}</span>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Collateral Business</label>
                    {ownedBusinesses?.length === 0 ? (
                      <div className="p-3 rounded-lg bg-error/10 border border-error/20">
                        <p className="text-xs text-error">You need to own at least one business to take a loan.</p>
                      </div>
                    ) : (
                      <select
                        value={collateralId}
                        onChange={e => { setCollateralId(e?.target?.value); if (errors?.collateral) setErrors(p => ({ ...p, collateral: '' })); }}
                        className={`w-full bg-surface border rounded-lg px-3 py-2.5 text-sm text-foreground focus:outline-none transition-colors ${
                          errors?.collateral ? 'border-error' : 'border-border focus:border-primary/50'
                        }`}
                      >
                        {ownedBusinesses?.map(b => (
                          <option key={b?.id} value={b?.id}>
                            {b?.name} (${b?.estimatedValue?.toLocaleString()})
                          </option>
                        ))}
                      </select>
                    )}
                    {errors?.collateral && <p className="mt-1 text-xs text-error flex items-center gap-1"><Icon name="AlertCircle" size={12} />{errors?.collateral}</p>}
                  </div>

                  <button
                    onClick={handleTakeLoan}
                    disabled={loading || ownedBusinesses?.length === 0}
                    className="w-full py-3 rounded-lg bg-primary text-zinc-950 font-semibold text-sm hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? <><Icon name="Loader2" size={16} className="animate-spin" />Processing...</> : <><Icon name="CreditCard" size={16} />Apply for Loan</>}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-3 rounded-lg bg-surface border border-border">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-muted-foreground">Remaining Balance</span>
                      <span className="text-error font-mono font-bold">${remainingBalance?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Your Cash</span>
                      <span className="text-success font-mono">${playerCash?.toLocaleString()}</span>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Repayment Amount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      <input
                        type="number"
                        value={repayAmount}
                        onChange={e => { setRepayAmount(e?.target?.value); if (errors?.repayAmount) setErrors(p => ({ ...p, repayAmount: '' })); }}
                        placeholder={`1 – ${remainingBalance?.toLocaleString()}`}
                        min={1}
                        max={Math.min(remainingBalance, playerCash)}
                        className={`w-full bg-surface border rounded-lg pl-7 pr-3 py-2.5 text-sm text-foreground focus:outline-none transition-colors ${
                          errors?.repayAmount ? 'border-error' : 'border-border focus:border-primary/50'
                        }`}
                      />
                    </div>
                    {errors?.repayAmount && <p className="mt-1 text-xs text-error flex items-center gap-1"><Icon name="AlertCircle" size={12} />{errors?.repayAmount}</p>}
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => setRepayAmount(String(Math.min(remainingBalance, playerCash)))} className="text-xs text-primary hover:underline">Pay Full</button>
                      <button onClick={() => setRepayAmount(String(Math.floor(Math.min(remainingBalance, playerCash) / 2)))} className="text-xs text-muted-foreground hover:text-foreground">Pay Half</button>
                    </div>
                  </div>

                  <button
                    onClick={handleRepayLoan}
                    disabled={loading}
                    className="w-full py-3 rounded-lg bg-success text-white font-semibold text-sm hover:bg-success/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? <><Icon name="Loader2" size={16} className="animate-spin" />Processing...</> : <><Icon name="CheckCircle" size={16} />Make Payment</>}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoanModal;
